import { useRef, useState } from 'react';

import { Button } from '../components/Button.js';
import { Icon, WavingHandIcon } from '../components/Icon.js';
import type { ChannelId, InboxItem } from '../fixtures/domain.js';
import { isJobBounded, type HiringStage } from '../fixtures/rules.js';
import { plural } from '../util/format.js';
import { useDismiss } from '../util/useDismiss.js';
import { useUnloadGuard } from '../util/useUnloadGuard.js';
import { BULK_MAX, type BulkResult } from './bulkSend.js';
import { ListPicker } from './ListPicker.js';
import { ScopeBox } from './ScopeBox.js';
import styles from './BulkActionDialog.module.css';

/** The stage + End actions this dialog confirms — the "…" overflow's rows minus
 *  Send (BulkSendDialog owns that) and the list rows (which need no dialog). */
export type BulkDialogAction = HiringStage | 'end';

interface Copy {
  /** Destructive actions wear a red confirm. */
  danger?: boolean;
  /** End alone withholds the "Add to list": a list of chats on their way to
   *  deletion is a list of dead rows. */
  noList?: boolean;
  title: (count: number) => string;
  /** Reads under the title. Takes the reject count because End is the one action
   *  where what it costs the set is the whole point of the sentence. */
  subtitle: (count: number, rejects: number) => string;
  confirm: (count: number) => string;
  /** Reads in front of "3/22" while the run is going. */
  busyLabel: string;
  /** Heads the result view: "{doneLabel} {done} of {total}". */
  doneLabel: string;
  /** Present on the actions that carry a message the employer writes (End). */
  compose?: {
    title: string;
    subtitle: (count: number, rejects: number) => string;
    placeholder: string;
    /** The closing template seeded into the field. Takes whether every target is
     *  a job applicant — the reject wording is used only then, never on a mix. */
    draft: (allApplicants: boolean) => string;
  };
}

/**
 * The warm closing template seeded into the field, nameless because a bulk End
 * reaches many. `allApplicants` picks the application-rejection wording, used
 * only when EVERY target is a job applicant — a mix with a direct chat falls back
 * to the neutral close copy, so one shared message never tells a non-applicant
 * their "application" was turned down. The mock drafts this with AI
 * (`useClosingDraft`); the UI-only rewrite seeds it statically — the AI path
 * returns with the wiring epic, like the single-chat EndChatDialog.
 */
function closingTemplate(allApplicants: boolean): string {
  return allApplicants
    ? 'Hi there, thank you for your time and for sharing your experience with us. ' +
        "After careful consideration, we've decided not to move forward with your application " +
        'for this role. We truly appreciate your interest and wish you all the best in your search.'
    : 'Hi there, thanks so much for chatting with us — it was great connecting. ' +
        "We'll keep your details on hand and reach out if a suitable role comes up. " +
        'Wishing you all the best!';
}

const COPY: Record<BulkDialogAction, Copy> = {
  shortlisted: {
    title: (count) => `Shortlisting ${plural(count, 'jobseeker')}`,
    subtitle: () => 'A stage can be changed later, but never removed.',
    confirm: (count) => `Shortlist ${count}`,
    busyLabel: 'Marking',
    doneLabel: 'Shortlisted',
  },
  interview: {
    title: (count) => `Marking ${plural(count, 'jobseeker')} as interviewed`,
    subtitle: () => 'A stage can be changed later, but never removed.',
    confirm: (count) => `Mark ${count} interviewed`,
    busyLabel: 'Marking',
    doneLabel: 'Marked interviewed',
  },
  hired: {
    title: (count) => `Marking ${plural(count, 'jobseeker')} as hired`,
    subtitle: () => 'A stage can be changed later, but never removed.',
    confirm: (count) => `Mark ${count} hired`,
    busyLabel: 'Marking',
    doneLabel: 'Marked hired',
  },
  end: {
    danger: true,
    noList: true,
    title: (count) => `Ending ${plural(count, 'conversation')}`,
    subtitle: () => 'And send a closing message to them.',
    confirm: (count) => `End ${count}`,
    busyLabel: 'Ending',
    doneLabel: 'Ended',
    compose: {
      title: 'Send a closing message',
      subtitle: (_count, rejects) =>
        rejects > 0
          ? `This ends the chats and rejects ${plural(rejects, 'jobseeker')}.`
          : 'This ends the chats.',
      placeholder: 'Keep it short and respectful…',
      draft: closingTemplate,
    },
  },
};

interface BulkActionDialogProps {
  action: BulkDialogAction;
  /** The chats the action lands on — already the eligible subset (see `bulkTargets`),
   *  in list order, for the ScopeBox to show who it reaches. */
  targets: InboxItem[];
  /** Picked chats it leaves alone (a stage skips the direct ones). */
  skipped: number;
  /** For End: how many of the targets are also rejected (a job behind them). */
  rejects: number;
  /** Runs the action; resolves with the per-channel outcome. `onProgress` reports
   *  how many have landed, for the bar. Text is the closing message for End, empty
   *  for the stages. */
  onConfirm: (
    text: string,
    onProgress: (done: number, total: number) => void,
  ) => Promise<BulkResult<ChannelId>>;
  /** Close the dialog. `done` is true once a run has landed something, so the
   *  caller clears the selection then but leaves it on a plain cancel. */
  onClose: (done: boolean) => void;
}

/**
 * Confirm a hiring stage or End over the picked set, in the two steps the
 * decision is made in: who first — the faces it reaches — then, for End, the
 * closing message. Stages have nothing to draft and so end at step one, but they
 * still get it: a stage never comes back off. Mirrors the design mock's flow;
 * shares the run and the result view with BulkSendDialog.
 */
export function BulkActionDialog({
  action,
  targets,
  skipped,
  rejects,
  onConfirm,
  onClose,
}: BulkActionDialogProps) {
  const copy = COPY[action];
  const count = targets.length;
  const channelIds = targets.map((item) => item.channel.id);
  const hasCompose = copy.compose != null; // End writes a closing message; a stage does not
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState('');
  const [edited, setEdited] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<BulkResult<ChannelId> | null>(null);
  const sentRef = useRef('');
  const running = progress !== null;
  // The run is a loop in this page: leaving takes the rest of it with them.
  useUnloadGuard(running);

  // The reject wording names an "application", so it is used only when every
  // target is a job applicant (each ends up rejected — now, or already); a mix
  // with a direct chat falls back to the neutral copy, so a non-applicant is
  // never told their application was turned down.
  const applicantsOnly = count > 0 && targets.every((item) => isJobBounded(item.channel));
  // Derived every render, not frozen at mount: a background re-read that reshapes
  // the batch re-picks the template — until the employer edits it, then theirs
  // stands. But while a run is in flight the field records what was sent, not a
  // live re-derive of a batch that shifted after the click — it freezes to the
  // text `run` captured.
  const composed = edited ? draft : copy.compose ? copy.compose.draft(applicantsOnly) : '';
  const text = running ? sentRef.current : composed;

  // Clear the selection only once something actually landed; a total failure
  // keeps it so a retry is one tap.
  const close = () => onClose(result !== null && result.done > 0);

  // Never yank the dialog mid-run: a half-finished batch that vanishes leaves the
  // employer unsure what actually happened.
  useDismiss(true, () => {
    if (!running) close();
  });

  // Over the ceiling, block rather than silently dropping the overflow — runBulk's
  // slice would otherwise leave those chats in neither the done nor failed count.
  const overCap = count > BULK_MAX;
  const canConfirm = count > 0 && !running && !overCap && (!hasCompose || text.trim() !== '');

  const run = async () => {
    if (!canConfirm) return;
    sentRef.current = text;
    setProgress({ done: 0, total: count });
    try {
      setResult(await onConfirm(text.trim(), (done, total) => setProgress({ done, total })));
    } finally {
      setProgress(null);
    }
  };

  const runOverlay = progress && (
    <div className={styles.run}>
      <div className={styles.runTrack}>
        <div
          className={styles.runBar}
          style={{ width: `${(progress.done / progress.total) * 100}%` }}
        />
      </div>
      <p className={styles.runLabel}>
        {copy.busyLabel} {progress.done}/{progress.total}
      </p>
    </div>
  );

  return (
    <div className={styles.overlay} onMouseDown={() => !running && close()}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {result ? (
          <>
            <div className={styles.headerPlain}>
              <h2 className={styles.title}>
                {copy.doneLabel} {result.done} of {result.done + result.failed.length}
              </h2>
              {result.failed.length > 0 && (
                <p className={styles.failed}>
                  {result.failed.length} couldn't be done — try those again from their own threads.
                </p>
              )}
            </div>
            <div className={styles.footer}>
              <Button variant="primary" onClick={close}>
                Done
              </Button>
            </div>
          </>
        ) : composing && copy.compose ? (
          <>
            <div className={styles.header}>
              <span className={styles.iconBadge}>
                <WavingHandIcon size={20} />
              </span>
              <div className={styles.heading}>
                <h2 className={styles.title}>{copy.compose.title}</h2>
                <p className={styles.subtitle}>{copy.compose.subtitle(count, rejects)}</p>
              </div>
            </div>
            <div className={styles.body}>
              <div className={styles.composeSlot}>
                <div className={styles.fieldHead}>
                  {edited ? (
                    <span className={styles.fieldLabel}>Message</span>
                  ) : (
                    <span className={styles.source}>
                      <Icon name="bolt" size={13} />
                      Template. Edit before sending
                    </span>
                  )}
                </div>
                <textarea
                  className={styles.textarea}
                  value={text}
                  disabled={running}
                  rows={5}
                  onChange={(e) => {
                    setDraft(e.target.value);
                    setEdited(true);
                  }}
                  placeholder={copy.compose.placeholder}
                  autoFocus
                />
                {runOverlay}
              </div>
              {count === 0 && (
                <p className={styles.overCap}>
                  These conversations left your inbox — nothing here to act on. Close and pick
                  again.
                </p>
              )}
            </div>
            <div className={styles.footer}>
              <Button variant="ghost" onClick={() => setComposing(false)} disabled={running}>
                Back
              </Button>
              <Button variant="danger" onClick={run} disabled={!canConfirm}>
                {running ? `${copy.busyLabel}…` : copy.confirm(count)}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className={styles.headerPlain}>
              <h2 className={styles.title}>{copy.title(count)}</h2>
              <p className={styles.subtitle}>{copy.subtitle(count, rejects)}</p>
            </div>
            <div className={styles.body}>
              {/* Named for what they are, not what they lack — the inbox filter
                  above already calls them Direct Messages. Only on the stages,
                  the one action that leaves part of the selection alone. */}
              {skipped > 0 && !hasCompose && (
                <p className={styles.skipped}>
                  <Icon name="info" size={14} />
                  <span>Skipping {plural(skipped, 'direct message')}.</span>
                </p>
              )}
              <ScopeBox
                targets={targets}
                foot={!copy.noList && <ListPicker channelIds={channelIds} />}
                overlay={runOverlay}
              />
              {overCap && (
                <p className={styles.overCap}>
                  You can act on up to {BULK_MAX} at once — deselect {count - BULK_MAX} to continue.
                </p>
              )}
              {count === 0 && (
                <p className={styles.overCap}>
                  These conversations left your inbox — nothing here to act on. Close and pick
                  again.
                </p>
              )}
            </div>
            <div className={styles.footer}>
              <Button variant="ghost" onClick={close} disabled={running}>
                Cancel
              </Button>
              {hasCompose ? (
                <Button
                  variant="primary"
                  onClick={() => setComposing(true)}
                  disabled={overCap || count === 0}
                >
                  Next
                </Button>
              ) : (
                <Button
                  variant={copy.danger ? 'danger' : 'primary'}
                  onClick={run}
                  disabled={!canConfirm}
                >
                  {running ? `${copy.busyLabel}…` : copy.confirm(count)}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
